import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { Markdown } from "@/components/ai/chat/Markdown";
import { cn } from "@/components/podkit/lib/cn";
import type { UserMessage } from "@/hooks/use-stream-conversation";
import { IconClock } from "central-icons/IconClock";
import { FileText } from "lucide-react";
import { type FC } from "react";

// Command prefixes for autonomous modes
const RALPH_COMMAND = "/ona:ralph";
const SPEC_COMMAND = "/ona:spec";

type AgentModePrefix = "ralph" | "spec" | null;

/**
 * Parses agent mode command prefix from content.
 * Returns both the detected mode and the content with prefix stripped.
 */
function parseAgentModePrefix(content: string): { mode: AgentModePrefix; strippedContent: string } {
    // Check Ralph prefix
    if (content.startsWith(RALPH_COMMAND)) {
        const remainder = content.slice(RALPH_COMMAND.length);
        if (remainder === "" || remainder.startsWith(" ")) {
            return { mode: "ralph", strippedContent: remainder.trimStart() };
        }
    }
    // Check Spec prefix
    if (content.startsWith(SPEC_COMMAND)) {
        const remainder = content.slice(SPEC_COMMAND.length);
        if (remainder === "" || remainder.startsWith(" ")) {
            return { mode: "spec", strippedContent: remainder.trimStart() };
        }
    }
    return { mode: null, strippedContent: content };
}

const AgentModeIcon: FC<{ mode: AgentModePrefix }> = ({ mode }) => {
    switch (mode) {
        case "ralph":
            return <IconRalphWiggum size={20} className="mt-[1px] shrink-0 text-content-orange" />;
        case "spec":
            return <FileText size={20} className="mt-[1px] shrink-0 text-content-brand" />;
        default:
            return null;
    }
};

export const UserMsg: FC<{
    message: UserMessage;
    isPending: boolean;
}> = ({ message, isPending }) => {
    // Check if any fragment has an agent mode prefix
    const agentMode = message.fragments.reduce<AgentModePrefix>((found, fragment) => {
        if (found) return found;
        if (fragment.type === "text") {
            return parseAgentModePrefix(fragment.content).mode;
        }
        return null;
    }, null);

    return (
        // the class user-message is used within the markdown component to style some markdown elements differently when presented
        // inside a user message.
        <div
            className="user-message flex w-fit max-w-full gap-2 self-end"
            data-message-id={`${message.type}-${message.id}`}
        >
            <div
                className={cn(
                    "min-w-0 flex-1 rounded-xl border border-border-subtle bg-surface-muted px-3 py-2 text-base text-content-primary transition-opacity",
                    {
                        "opacity-50": isPending,
                    },
                )}
            >
                <div className="flex gap-2">
                    {isPending && <IconClock className="mt-[1px] shrink-0 text-content-primary" size="14" />}
                    <AgentModeIcon mode={agentMode} />
                    <div className="flex min-w-0 flex-1 flex-col">
                        {message.fragments.map((fragment) => {
                            switch (fragment.type) {
                                case "text": {
                                    const { strippedContent } = parseAgentModePrefix(fragment.content);
                                    return <Markdown content={strippedContent} key={fragment.id} />;
                                }
                            }
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
